import get from 'lodash/get';
import omit from 'lodash/omit';
import { action, computed, makeObservable, observable } from 'mobx';
import { getResourceGallery } from 'stores/offeringUtils';
import { IOfferingInfo, IOfferingTitle, ISponsorInfo } from 'utils/globalTypes';
import { getObjContentList, getObjProperty, isNotEmpty } from 'utils/object';

import { calcInvestmentDetails } from './financeUtils';
import { IAssetData, IInvestmentContent, IOfferingData, ISponsorData } from './interfaces';
import { IOffering } from './networkInterfaces';

export const SPONSOR_INFO_VALUES: Record<string, string> = {
  name: 'sponsor.name',
  description: 'sponsor.description',
  logo: 'sponsor.logo',
  website: 'sponsor.website'
};

export const DEAL_TITLE: Record<string, string> = {
  name: 'offering.name',
  description: 'offering.description',
  type: 'asset.asset_type'
};

class ContentClientStore {
  offeringResponse: IOffering = {};
  contentData: IInvestmentContent = {};
  offeringInfo: IOfferingInfo = {};

  constructor() {
    makeObservable(this, {
      contentData: observable,
      offeringInfo: observable,
      offeringResponse: observable,

      clearStore: action,
      setOfferingData: action,

      getAllContentData: computed,
      getOfferingInfo: computed,
      getOfferingTitle: computed,
      getSponsorData: computed
    });
  }

  get getAllContentData() {
    return this.contentData;
  }

  get getOfferingTitle() {
    const dealTitleFields = this.fieldsMapper(DEAL_TITLE);

    const offeringId = this.offeringResponse?.id;

    return {
      ...dealTitleFields,
      offeringId
    } as IOfferingTitle;
  }

  get getOfferingInfo() {
    return this.offeringInfo;
  }

  get getOfferingResponse() {
    return this.offeringResponse;
  }

  get getDealGallery() {
    const offering_media = getResourceGallery(this.contentData, 'offering.media');
    if (isNotEmpty(offering_media)) return offering_media;

    return getResourceGallery(this.contentData, 'asset.media');
  }

  get getSponsorData() {
    return this.fieldsMapper(SPONSOR_INFO_VALUES) as ISponsorInfo;
  }

  setOfferingData(offeringData: IOffering, content?: Record<string, unknown>) {
    this.offeringResponse = offeringData;
    const contentData: IInvestmentContent = {};
    contentData.id = offeringData?.id;

    contentData.offering = omit(
      content?.offering as Record<string, unknown>,
      'sponsor'
    ) as IOfferingData;
    contentData.asset = content?.asset as IAssetData;
    contentData.sponsor = {
      ...(content?.sponsor as Record<string, unknown>),
      ...(get(content?.offering, 'sponsor') as Record<string, unknown>)
    } as ISponsorData;
    contentData.syndicator = content?.syndicator as ISponsorData;
    contentData.operator = content?.operator as ISponsorData;

    this.contentData = contentData;
    this.setOfferingInfo();
  }

  setOfferingInfo() {
    this.offeringInfo = calcInvestmentDetails({
      ...this.offeringResponse
    });
  }

  clearStore() {
    this.contentData = {};
    this.offeringInfo = {};
  }

  fieldsMapper(fields: Record<string, string>) {
    return Object.keys(fields).reduce<Record<string, unknown>>((result, key) => {
      const value = this.getContentField(fields[key]);
      if (value) {
        result[key] = value;
      }
      return result;
    }, {});
  }

  getContentList(path: string) {
    return getObjContentList(this.contentData, path);
  }

  getContentField(path: string) {
    return getObjProperty(this.contentData, path);
  }
}

export const ContentClientStoreInstance = new ContentClientStore();

export default ContentClientStore;
